/**
 * ============================================================================
 *  File        : frontend/src/pages/CasesListPage.tsx
 *  Module      : pages
 *  Purpose     : /cases 案件列表（spec §8.2）— shadcn Table + TanStack Query 分頁
 *  Author      : EF Engineering
 *  Created     : 2026-05-31
 *  Updated     : 2026-05-31
 *  License     : Proprietary - ECO FAITH Platform v3.0
 * ============================================================================
 */

import { useState, type ReactElement } from 'react'
import { useQuery, keepPreviousData } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'
import { listCases } from '../api/cases'
import { ApiError } from '../api/client'
import { usePermissions } from '../hooks/usePermissions'
import { PERMISSIONS } from '../lib/permissions'
import { Badge } from '../components/ui/badge'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table'
import { Skeleton } from '../components/ui/skeleton'

interface PageProps {
  t: (key: string) => string
}

const PER_PAGE = 20

export function CasesListPage({ t }: PageProps): ReactElement {
  const navigate = useNavigate()
  const { can } = usePermissions()
  const canCreate = can(PERMISSIONS.caseCreate)
  const [page, setPage] = useState(1)
  const [caseNo, setCaseNo] = useState('')
  const [search, setSearch] = useState('')

  const query = useQuery({
    queryKey: ['cases', { page, search }],
    queryFn: () => listCases({ page, perPage: PER_PAGE, caseNo: search || undefined }),
    placeholderData: keepPreviousData,
  })

  const cases = query.data?.data ?? []
  const total = query.data?.meta?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE))

  return (
    <section className="page-stack">
      <header className="page-heading flex items-start justify-between gap-4">
        <div>
          <span className="eyebrow">{t('nav.cases')}</span>
          <h1>{t('case.list.title')}</h1>
          <p>{t('case.list.subtitle')}</p>
        </div>
        {canCreate ? (
          <Button asChild>
            <Link to="/cases/new">{t('case.new.title')}</Link>
          </Button>
        ) : null}
      </header>

      <form
        className="flex gap-2"
        onSubmit={(e) => {
          e.preventDefault()
          setPage(1)
          setSearch(caseNo.trim())
        }}
      >
        <Input
          value={caseNo}
          onChange={(e) => setCaseNo(e.target.value)}
          placeholder={t('case.list.searchCaseNo')}
          className="max-w-xs"
          aria-label={t('case.list.searchCaseNo')}
        />
        <Button type="submit" variant="outline">
          {t('common.search')}
        </Button>
      </form>

      {query.isError ? (
        <p className="text-sm text-destructive">
          {t('case.list.loadError')}
          {query.error instanceof ApiError ? `（${query.error.message}）` : ''}
        </p>
      ) : null}

      <div className="rounded-md border border-border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('field.caseNo')}</TableHead>
              <TableHead>{t('field.caseTitle')}</TableHead>
              <TableHead>{t('field.status')}</TableHead>
              <TableHead>{t('field.workflowState')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {query.isPending ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={`skeleton-${i}`}>
                  {Array.from({ length: 4 }).map((__, j) => (
                    <TableCell key={`cell-${i}-${j}`}>
                      <Skeleton className="h-4 w-full" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : cases.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center text-muted-foreground">
                  {t('case.list.empty')}
                </TableCell>
              </TableRow>
            ) : (
              cases.map((c) => (
                <TableRow
                  key={c.id}
                  className="cursor-pointer"
                  onClick={() => void navigate(`/cases/${c.id}`)}
                >
                  <TableCell className="font-medium">{c.case_no}</TableCell>
                  <TableCell>{c.title}</TableCell>
                  <TableCell>
                    <Badge variant="secondary">{c.status}</Badge>
                  </TableCell>
                  <TableCell>{c.workflow_state ?? '—'}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-between text-sm text-muted-foreground">
        <span>{t('case.list.total').replace('{total}', String(total))}</span>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1 || query.isFetching}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            {t('case.list.prev')}
          </Button>
          <span>
            {t('case.list.page').replace('{page}', String(page))} / {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages || query.isFetching}
            onClick={() => setPage((p) => p + 1)}
          >
            {t('case.list.next')}
          </Button>
        </div>
      </div>
    </section>
  )
}
